/**
 * useAdaptivePath
 * ──────────────────────────────────────────────────────────────────────────
 * Consomme le harvestData accumulé dans ScenarioPlayer et produit :
 *   - adaptedSteps  : liste de steps réordonnés/filtrés selon le profil détecté
 *   - learnerProfile: { type, label, pesaParams, emotionSignals }
 *   - profileBadge  : label court affichable dans l'UI
 *   - cardFilter    : fn(card) → bool   utilisée dans progressive-reveal
 *
 * Règles de profilage (issues des harvestAnalysis / emotionMapping du module) :
 *
 *   ÉMOTIONS (emotional-checklist)
 *   ─────────────────────────────
 *   Compassion / Respect     → profil "sensible"   → renforcer bienveillance
 *   Gêne / Crainte           → profil "éthique"    → insister sur dignité
 *   Impuissance / Tristesse  → profil "émotionnel" → ancrage rogérien fort
 *
 *   NOTION PRIORITAIRE (progressive-reveal finalQuestion)
 *   ──────────────────────────────────────────────────────
 *   "publics"  → curieux/terrain   → privilegier témoignages et cas concrets
 *   "droits"   → juridique         → privilegier cards conceptualDepth >= 2
 *   "projet"   → pragmatique       → privilegier transferExample et pesaActionId
 *
 *   DIAGNOSTIC SCORE
 *   ─────────────────
 *   >= 80% → mode "renforcement" : skip trigger + emotional, direct reveal
 *   < 50%  → mode "découverte"  : séquence complète + theory-invitation forcée
 *
 * Usage dans ScenarioPlayer :
 *
 *   const { adaptedSteps, cardFilter, learnerProfile } = useAdaptivePath({
 *     steps: content.steps,
 *     harvestedData,
 *     diagnosticScore,       // passé depuis handleDiagnosticComplete
 *   });
 *   // Remplace `steps` par `adaptedSteps` dans la render loop
 *   // Passe `cardFilter` au bloc progressive-reveal
 */

import { useMemo } from 'react';

// ─── Constantes de profil ──────────────────────────────────────────────────

const EMOTION_SIGNALS = {
  sensible:    ['Compassion', 'Respect', 'Tristesse'],
  ethique:     ['Gêne', 'Crainte de mal faire'],
  emotionnel:  ['Impuissance', 'Sentiment d\'abandon', 'Irritabilité'],
};

const NOTION_PROFILES = {
  publics: 'curieux',
  droits:  'juridique',
  projet:  'pragmatique',
};

// Pondération : combien d'émotions d'un type pour déclencher le profil
const EMOTION_THRESHOLD = 1;

// ─── Détection du profil dominant ─────────────────────────────────────────

function detectEmotionProfile(harvestedData) {
  // Agréger toutes les clés d'émotions dans le harvest
  const allSelected = Object.values(harvestedData)
    .filter(v => v?.type === 'emotions' && Array.isArray(v.selected))
    .flatMap(v => v.selected);

  const scores = {};
  for (const [profile, emotions] of Object.entries(EMOTION_SIGNALS)) {
    scores[profile] = allSelected.filter(e => emotions.includes(e)).length;
  }

  // Profil dominant = max score >= threshold, null si aucun
  const dominant = Object.entries(scores)
    .filter(([, score]) => score >= EMOTION_THRESHOLD)
    .sort(([, a], [, b]) => b - a)[0];

  return dominant ? dominant[0] : null;
}

function detectNotionProfile(harvestedData) {
  // Chercher les réponses aux finalQuestion des progressive-reveal
  const notionKeys = Object.entries(harvestedData).filter(
    ([k]) => k.includes('notion-prioritaire') || k.includes('besoin-neglige')
  );
  for (const [, v] of notionKeys) {
    const text = (typeof v === 'string' ? v : v?.text || '').toLowerCase();
    for (const [notion, profile] of Object.entries(NOTION_PROFILES)) {
      if (text.includes(notion)) return profile;
    }
  }
  return null;
}

function buildLearnerProfile(harvestedData, diagnosticScore) {
  const emotionProfile = detectEmotionProfile(harvestedData);
  const notionProfile  = detectNotionProfile(harvestedData);
  const mode = diagnosticScore >= 80 ? 'renforcement'
             : diagnosticScore < 50  ? 'decouverte'
             : 'standard';

  // Priorité : notion > émotion > null
  const profileType = notionProfile || emotionProfile || 'standard';

  const LABELS = {
    curieux:     'Profil Terrain',
    juridique:   'Profil Cadre légal',
    pragmatique: 'Profil Action',
    sensible:    'Profil Bienveillant',
    ethique:     'Profil Éthique',
    emotionnel:  'Profil Ancrage rogérien',
    standard:    'Profil Standard',
  };

  const PESA_PARAMS = {
    curieux:     ['bienEtre', 'autonomie'],
    juridique:   ['dignite', 'ethique'],
    pragmatique: ['autonomie', 'collaboration'],
    sensible:    ['bienEtre', 'dignite'],
    ethique:     ['ethique', 'dignite'],
    emotionnel:  ['bienEtre', 'autonomie'],
    standard:    ['bienEtre'],
  };

  return {
    type:          profileType,
    label:         LABELS[profileType] || 'Standard',
    mode,
    pesaParams:    PESA_PARAMS[profileType] || ['bienEtre'],
    emotionSignal: emotionProfile,
    notionSignal:  notionProfile,
    diagnosticScore,
  };
}

// ─── Filtrage des cards selon le profil ───────────────────────────────────

function buildCardFilter(profile) {
  /**
   * Retourne true si la card doit être affichée pour ce profil.
   *
   * Règles :
   *  - Profil "juridique"   → affiche toutes les cards de conceptualDepth >= 1
   *  - Profil "pragmatique" → met en avant les cards avec pesaActionId ou transferExample
   *  - Profil "curieux"     → met en avant les cards avec modal.contentSections[type=video]
   *  - Mode "renforcement"  → masque les cards de conceptualDepth === 1 (trop basiques)
   *  - Par défaut           → tout afficher
   *
   * Note : on ne masque jamais toutes les cards — minimum 1 toujours visible.
   */
  return function cardFilter(card, index, allCards) {
    // Toujours afficher si une seule card
    if (allCards.length <= 1) return true;

    if (profile.mode === 'renforcement' && card.conceptualDepth === 1 && allCards.length > 1) {
      // En renforcement, skip les cards purement introductives sauf la première
      if (index === 0) return true;
      return false;
    }

    // Profil juridique : toujours tout montrer, depth >= 2 en premier (réordonnement géré dans adaptedSteps)
    if (profile.type === 'juridique') return true;

    // Profil pragmatique : masquer les cards sans transferExample ni pesaActionId (depth 1 verbeux)
    if (profile.type === 'pragmatique') {
      if (card.conceptualDepth >= 2) return true;
      if (card.pesaActionId || card.transferExample) return true;
      if (index === 0) return true; // toujours au moins la première
      return true; // sécurité : on ne masque pas pour l'instant, on réordonne
    }

    return true;
  };
}

// ─── Réordonnement des cards selon le profil ──────────────────────────────

function reorderCards(cards, profile) {
  if (!cards || cards.length === 0) return cards;

  const clone = [...cards];

  if (profile.type === 'juridique') {
    // Mettre les cards avec keyQuote ou loi en premier
    return clone.sort((a, b) => {
      const aHasLaw = a.keyQuote?.author?.toLowerCase().includes('loi') || false;
      const bHasLaw = b.keyQuote?.author?.toLowerCase().includes('loi') || false;
      return (bHasLaw ? 1 : 0) - (aHasLaw ? 1 : 0);
    });
  }

  if (profile.type === 'pragmatique') {
    // Mettre les cards avec pesaActionId en premier
    return clone.sort((a, b) => {
      const aAction = a.pesaActionId ? 1 : 0;
      const bAction = b.pesaActionId ? 1 : 0;
      return bAction - aAction;
    });
  }

  if (profile.type === 'curieux') {
    // Mettre les cards avec video dans la modal en premier
    const hasVideo = (card) =>
      card.modal?.contentSections?.some(s => s.type === 'video') || false;
    return clone.sort((a, b) => (hasVideo(b) ? 1 : 0) - (hasVideo(a) ? 1 : 0));
  }

  return clone;
}

// ─── Adaptation des steps selon le mode ───────────────────────────────────

/**
 * TYPES DE STEPS et leur comportement selon le mode :
 *
 * mode "renforcement" (score >= 80) :
 *   - skip : experiential-trigger, emotional-checklist
 *   - garde : progressive-reveal (mais cards filtrées), micro-quiz,
 *             personal-synthesis, empowering-closure, theory-invitation
 *
 * mode "decouverte" (score < 50) :
 *   - garde tout
 *   - force theory-invitation à être non-skippable (adaptedStep.forceEngaged = true)
 *
 * mode "standard" :
 *   - aucun changement structurel
 */

const SKIP_IN_RENFORCEMENT = new Set(['experiential-trigger', 'trigger']);

function adaptSteps(steps, profile) {
  if (!steps || steps.length === 0) return steps;

  return steps
    .filter(step => {
      if (profile.mode === 'renforcement') {
        return !SKIP_IN_RENFORCEMENT.has(step.type);
      }
      return true;
    })
    .map(step => {
      // Pour les progressive-reveal, injecter les cards réordonnées
      if (step.type === 'progressive-reveal' && step.cards) {
        const filtered = step.cards.filter(
          (card, i, arr) => buildCardFilter(profile)(card, i, arr)
        );
        const reordered = reorderCards(filtered, profile);
        return { ...step, cards: reordered, _adaptedBy: profile.type };
      }

      // Pour les theory-invitation en mode découverte : forcer l'engagement
      if (step.type === 'theory-invitation' && profile.mode === 'decouverte') {
        return { ...step, forceEngaged: true };
      }

      return step;
    });
}

// ─── Hook principal ────────────────────────────────────────────────────────

/**
 * @param {object} params
 * @param {Array}  params.steps          — steps bruts du content-step
 * @param {object} params.harvestedData  — état courant du harvest (depuis ScenarioPlayer)
 * @param {number} params.diagnosticScore — score du quiz diagnostic (0–100), default 60
 */
export function useAdaptivePath({ steps = [], harvestedData = {}, diagnosticScore = 60 }) {
  const learnerProfile = useMemo(
    () => buildLearnerProfile(harvestedData, diagnosticScore),
    // Recalcul à chaque nouvel élément récolté ou changement de score
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(harvestedData), diagnosticScore]
  );

  const adaptedSteps = useMemo(
    () => adaptSteps(steps, learnerProfile),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(steps), learnerProfile.type, learnerProfile.mode]
  );

  const cardFilter = useMemo(
    () => buildCardFilter(learnerProfile),
    [learnerProfile.type, learnerProfile.mode]
  );

  return {
    adaptedSteps,
    cardFilter,
    learnerProfile,
    profileBadge: learnerProfile.label,
    isReinforcement: learnerProfile.mode === 'renforcement',
    isDiscovery:     learnerProfile.mode === 'decouverte',
  };
}

// ─── Export des utilitaires pour les tests ────────────────────────────────
export {
    adaptSteps, buildCardFilter, buildLearnerProfile,
    detectEmotionProfile,
    detectNotionProfile, reorderCards
};
